import { ChangeDetectorRef, Directive, inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { EmptyError, Observable, firstValueFrom, isObservable } from 'rxjs';
import { ToasterService } from 'src/core/services/toaster/toaster.service';
import { LocalizationService } from '@abp/ng.core';
import { SettingsDto } from 'src/core/models/settings/settings.dto';
@Directive()
/** Use {@link FormActionsComponent} (`cx-form-actions`) for sticky Cancel/Save actions in form templates. */
export abstract class BaseFormComponent<TModel = any> implements OnInit {
  /** Form instance built in child via buildForm(). */
  form!: FormGroup;

  /** Has submit been attempted at least once? */
  submitted = false;

  /** True while submitRequest is running. */
  isSubmitting = false;

  /** Last submit error if any. */
  submitError: unknown | null = null;

  /** True if last submit finished successfully. */
  submitSuccess = false;

  /** Toaster service for showing notifications. */
  protected readonly toasterService = inject(ToasterService);

  /** Localization service for translating messages. */
  protected readonly localizationService = inject(LocalizationService);

  protected constructor(protected cdr: ChangeDetectorRef) {}

  // ---------- Lifecycle ----------

  ngOnInit(): void {
    this.form = this.buildForm();
    this.afterFormInit();
  }

  // ---------- To be implemented by child ----------

  /** Child must return the form definition here. */
  protected abstract buildForm(): FormGroup;

  /** Perform the submit action (service call, store dispatch, etc.). */
  protected abstract submitRequest(value: TModel): Observable<unknown> | Promise<unknown> | unknown;

  /** Optional hook after form creation. */
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected afterFormInit(): void {}

  /** Called when form is invalid on submit. */
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected onSubmitInvalid(): void {}

  /** Called after a successful submit. Override to customize behavior. */
  protected onSubmitSuccess(): void {
    this.toasterService.success(this.localizationService.instant('::SuccessfullySaved'));
  }

  /** Called when submitRequest rejects/throws. Override to customize behavior. */
  protected onSubmitError(_error: unknown): void {
    this.toasterService.error(this.localizationService.instant('::SaveFailed'));
  }

  /** Common validator set for URLs (http/https), optionally adds required. */
  protected urlValidators(required = true): ValidatorFn[] {
    const validators: ValidatorFn[] = [Validators.pattern(/^(https?:\/\/)[^\s/$.?#].[^\s]*$/i)];
    if (required) {
      validators.unshift(Validators.required);
    }
    return validators;
  }

  /**
   * Validator set for integration endpoints the backend calls with tenant credentials: https only, matching
   * the server-side rule (which additionally rejects local and private addresses).
   */
  protected httpsUrlValidators(required = true): ValidatorFn[] {
    const validators: ValidatorFn[] = [Validators.pattern(/^\s*https:\/\/[^\s/$.?#].[^\s]*\s*$/i)];
    if (required) {
      validators.unshift(Validators.required);
    }
    return validators;
  }

  /** Common validator set for emails, optionally adds required. */
  protected emailValidators(required = true): ValidatorFn[] {
    const validators: ValidatorFn[] = [Validators.email];
    if (required) {
      validators.unshift(Validators.required);
    }
    return validators;
  }

  /** Localized default message for invalid URLs. */
  protected getUrlInvalidMessage(): string {
    return this.localizationService.instant('::InvalidUrl');
  }

  /** Localized message for endpoints validated by {@link httpsUrlValidators}. */
  protected getHttpsUrlInvalidMessage(): string {
    return this.localizationService.instant('Settings::IntegrationEndpointUrlInvalid');
  }

  /** Localized default message for invalid emails. */
  protected getEmailInvalidMessage(): string {
    return this.localizationService.instant('::InvalidEmail');
  }

  /** Override if submit payload differs from form raw value. */
  protected getSubmitValue(): TModel {
    return this.form.getRawValue() as TModel;
  }

  // ---------- Submit flow ----------

  /** Bind to (ngSubmit) on your form. */
  onSubmit(): void {
    this.submitted = true;
    this.submitError = null;
    this.submitSuccess = false;

    this.form.markAllAsTouched();
    this.form.updateValueAndValidity({ onlySelf: false, emitEvent: false });

    if (this.form.invalid) {
      this.onSubmitInvalid();
      this.cdr.markForCheck();
      return;
    }

    const value = this.getSubmitValue();

    this.isSubmitting = true;
    this.cdr.markForCheck();

    const result = this.submitRequest(value);

    this.toPromise(result)
      .then(response => {
        this.isSubmitting = false;
        this.submitSuccess = true;
        this.onSubmitSuccess();
        this.cdr.markForCheck();
      })
      .catch(error => {
        this.isSubmitting = false;
        if (error instanceof EmptyError) {
          this.cdr.markForCheck();
          return;
        }
        this.submitError = error;
        this.onSubmitError(error);
        this.cdr.markForCheck();
      });
  }

  /** Reset form and submit state flags. */
  resetForm(value?: Partial<TModel>): void {
    if (value) {
      this.form.reset(value);
    } else {
      this.form.reset();
    }
    this.submitted = false;
    this.isSubmitting = false;
    this.submitError = null;
    this.submitSuccess = false;
    this.cdr.markForCheck();
  }

  // ---------- Template helpers ----------

  /** Check validity for entire form or specific control. */
  isValid(path?: string): boolean {
    if (!path) {
      return this.form.valid;
    }
    const control = this.getControl(path);
    return !!control && control.valid;
  }

  /** Convenience inverse of isValid. */
  isInvalid(path?: string): boolean {
    return !this.isValid(path);
  }

  /** Get raw value of form or a single control. */
  getValue<T = any>(path?: string): T | TModel | null {
    if (!path) {
      return this.form.getRawValue() as TModel;
    }
    const control = this.getControl(path);
    return (control?.value as T) ?? null;
  }

  /** Patch entire form or set a specific control value. */
  setValue(value: Partial<TModel>): void;
  setValue(path: string, value: any): void;
  setValue(pathOrValue: string | Partial<TModel>, maybeValue?: any): void {
    if (typeof pathOrValue === 'string') {
      const control = this.getControl(pathOrValue);
      if (control) {
        control.setValue(maybeValue);
        control.markAsDirty();
      }
    } else {
      this.form.patchValue(pathOrValue);
      this.form.markAsDirty();
    }
    this.cdr.markForCheck();
  }

  /** Enable/disable whole form or specific control(s). */
  setDisabled(disabled: boolean): void;
  setDisabled(path: string | string[], disabled: boolean): void;
  setDisabled(pathOrDisabled: boolean | string | string[], maybeDisabled?: boolean): void {
    if (typeof pathOrDisabled === 'boolean') {
      if (pathOrDisabled) {
        this.form.disable({ emitEvent: false });
      } else {
        this.form.enable({ emitEvent: false });
      }
      this.cdr.markForCheck();
      return;
    }

    const disabled = !!maybeDisabled;
    const paths = Array.isArray(pathOrDisabled) ? pathOrDisabled : [pathOrDisabled];

    paths.forEach(p => {
      const control = this.getControl(p);
      if (!control) {
        return;
      }
      if (disabled) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    });

    this.cdr.markForCheck();
  }

  /** Shortcuts for setDisabled. */
  disable(path?: string | string[]): void {
    if (path) {
      this.setDisabled(path, true);
    } else {
      this.setDisabled(true);
    }
  }

  enable(path?: string | string[]): void {
    if (path) {
      this.setDisabled(path, false);
    } else {
      this.setDisabled(false);
    }
  }

  /** Retrieve control by path (supports nesting). */
  getControl(path: string): AbstractControl | null {
    return this.form.get(path);
  }

  /** True when control is invalid and should show errors. */
  isControlInvalid(path: string): boolean {
    const control = this.getControl(path);
    if (!control) {
      return false;
    }

    const show = control.dirty || control.touched || this.submitted;
    return show && control.invalid;
  }

  /** Check if a control currently has the specified error. */
  hasError(path: string, errorCode: string): boolean {
    const control = this.getControl(path);
    if (!control) {
      return false;
    }

    const show = control.dirty || control.touched || this.submitted;
    if (!show) {
      return false;
    }

    return control.hasError(errorCode);
  }

  /** Return raw ValidationErrors for a control. */
  getErrors(path: string): ValidationErrors | null {
    const control = this.getControl(path);
    return control?.errors ?? null;
  }

  /** Return the first error key on a control, if any. */
  getFirstErrorKey(path: string): string | null {
    const errors = this.getErrors(path);
    if (!errors) {
      return null;
    }
    const keys = Object.keys(errors);
    return keys.length ? keys[0] : null;
  }

  protected convertToSettingsDto(
    formValue: TModel,
    mapping: Record<keyof TModel, string>
  ): SettingsDto[] {
    return (Object.entries(mapping) as Array<[keyof TModel, string]>).map(
      ([formKey, settingName]) => {
        const dto = new SettingsDto();
        dto.settingName = settingName;
        dto.settingValue = (formValue as any)[formKey] ?? '';
        return dto;
      }
    );
  }

  // ---------- Internal helper ----------

  private async toPromise<T>(value: Observable<T> | Promise<T> | T): Promise<T> {
    if (isObservable(value)) {
      return firstValueFrom(value);
    }
    return Promise.resolve(value);
  }
}
